import { z } from "zod";
import {
  AUTOMATION_ACTION_OPTIONS,
  AUTOMATION_TRIGGER_OPTIONS,
} from "@/lib/const";
import { flattenTriggers } from "@/lib/flattenTriggers";
import { flattenActions } from "@/lib/flattenActions";
import { hasContradictoryFixedValueConditions } from "@/lib/automationTriggerOptions";
import {
  GenerateOptions,
  resolveAutomationReferences,
} from "@/lib/automationExport";
import {
  Automation,
  BuilderTrigger,
  DynamicOptions,
} from "@/types/v2/Automation";

// triggerEventType/triggerFieldChange/triggerCondition (below) and
// actionEventType (on ActionSchema) are deliberately loose (`z.string()`
// rather than a hardcoded enum) because their valid values are governed by
// the trigger/action TEMPLATE (see validateAutomationForImport's tuple
// check), not by a fixed global set — the catalog evolves (triggers/actions
// get removed, e.g. the legacy "trigger-006") and a real export file can
// carry a value that was valid under a template that no longer exists.
// Hardcoding an enum here would kill the WHOLE FILE at parse for one
// removed/legacy value, before the per-automation business-validation layer
// (which already knows how to reject just that automation, with a clear
// reason) ever runs. Everything else on these schemas — `triggerObject`,
// `actionObject`, `triggersConditional` — has been a fixed, non-template
// literal/enum since inception and stays strict.
const TriggerSchema = z.object({
  templateId: z.string(),
  triggerObject: z.literal("card"),
  triggerEventType: z.string(),
  triggerField: z.string().nullable(),
  triggerFieldChange: z.string().nullable(),
  triggerCondition: z.string().nullable(),
  triggerValue: z.union([z.string(), z.number(), z.boolean()]).nullable(),
});

const ActionSchema = z.object({
  templateId: z.string(),
  actionObject: z.literal("card"),
  actionObjectIdentifier: z.string(),
  actionEventType: z.string(),
  actionField: z.string().nullable(),
  actionValue: z.union([z.string(), z.number()]).nullable(),
});

const ResolvedReferenceSchema = z.object({
  kind: z.enum(["trigger", "action"]),
  index: z.number(),
  templateId: z.string(),
  dynamicType: z.enum([
    "colors",
    "containers",
    "technicians",
    "serviceAdvisors",
    "tags",
  ]),
  value: z.string(),
  label: z.string(),
});

const ExportedAutomationSchema = z.object({
  // Optional: session-1's export code (deployed to `dev` before this field
  // existed) produced automations without `id`, and those files are already
  // in the wild. Missing id always plans as "create" (see
  // planAutomationImport) rather than crashing structural parse.
  id: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  enabled: z.boolean(),
  triggersConditional: z.enum(["AND", "OR"]),
  triggers: z.array(TriggerSchema),
  actions: z.array(ActionSchema),
  resolvedReferences: z.array(ResolvedReferenceSchema),
});

export const AutomationExportFileSchema = z.object({
  schemaVersion: z.literal(1),
  exportedAt: z.string(),
  sourceShopId: z.string(),
  automations: z.array(ExportedAutomationSchema),
});

export type ParsedAutomationExportFile = z.infer<
  typeof AutomationExportFileSchema
>;

const INVALID_FILE_ERROR =
  "This doesn't look like a valid automations file. Paste the JSON from your AI assistant, or choose a JSON file exported from Automations.";

// LLMs routinely wrap their JSON in ```json fences or surrounding prose, even
// when asked not to. Pull the JSON out of a pasted blob before parsing: prefer
// a fenced code block, else fall back to the outermost { … } span. A clean
// JSON paste (or a real export file) passes through unchanged.
export function extractJsonPayload(raw: string): string {
  const trimmed = raw.trim();
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fence) return fence[1].trim();
  const first = trimmed.indexOf("{");
  const last = trimmed.lastIndexOf("}");
  if (first !== -1 && last > first) return trimmed.slice(first, last + 1);
  return trimmed;
}

// Malformed JSON and wrong-shape JSON collapse into one user-facing error —
// the caller only needs to know the file isn't usable.
export function parseAutomationImportFile(
  raw: string
): { file: ParsedAutomationExportFile } | { error: string } {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { error: INVALID_FILE_ERROR };
  }
  const result = AutomationExportFileSchema.safeParse(json);
  if (!result.success) {
    return { error: INVALID_FILE_ERROR };
  }
  return { file: result.data };
}

export interface RejectedAutomation {
  automation: ParsedAutomationExportFile["automations"][number];
  reasons: string[];
}

export function validateAutomationForImport(
  automation: ParsedAutomationExportFile["automations"][number],
  generateOptions: GenerateOptions
): string[] {
  const reasons: string[] = [];
  const flatTriggers = flattenTriggers(AUTOMATION_TRIGGER_OPTIONS);
  const flatActions = flattenActions(AUTOMATION_ACTION_OPTIONS);

  // Accumulated alongside the per-trigger checks below (which already
  // resolve each trigger's template) for the cross-trigger contradiction
  // check that follows the loop — avoids re-resolving every template a
  // second time in a separate pass.
  const fixedValueShapes: {
    triggerField: string | null;
    triggerFieldChange?: string | null;
    triggerValueEditable: boolean;
    triggerValue: string | number | boolean | null;
  }[] = [];

  automation.triggers.forEach((trigger, i) => {
    const template = flatTriggers.find((t) => t.id === trigger.templateId);
    if (!template) {
      reasons.push(
        `Trigger ${i + 1} uses a removed trigger type ("${trigger.templateId}")`
      );
      return;
    }
    fixedValueShapes.push({
      triggerField: trigger.triggerField,
      triggerFieldChange: trigger.triggerFieldChange,
      triggerValueEditable: template.triggerValueEditable,
      triggerValue: trigger.triggerValue,
    });
    const fieldConsistent = template.triggerFieldOptions?.length
      ? template.triggerFieldOptions.some((f) => f === trigger.triggerField)
      : template.triggerField === trigger.triggerField;
    if (!fieldConsistent) {
      reasons.push(
        `Trigger ${i + 1} has a field ("${trigger.triggerField}") that doesn't match its trigger type ("${trigger.templateId}")`
      );
    }

    // triggerEventType is the only one of these four knobs that's optional
    // on BuilderTrigger — every template in the shipped catalog sets it, but
    // a template that genuinely leaves it unconstrained (undefined, no
    // options list) shouldn't reject on that basis alone.
    const eventTypeConsistent = template.triggerEventTypeOptions?.length
      ? template.triggerEventTypeOptions.some(
          (e) => e === trigger.triggerEventType
        )
      : template.triggerEventType === undefined
        ? true
        : template.triggerEventType === trigger.triggerEventType;
    if (!eventTypeConsistent) {
      reasons.push(
        `Trigger ${i + 1} has an event type ("${trigger.triggerEventType}") that doesn't match its trigger type ("${trigger.templateId}")`
      );
    }

    const fieldChangeConsistent = template.triggerFieldChangeOptions?.length
      ? template.triggerFieldChangeOptions.some(
          (f) => f === trigger.triggerFieldChange
        )
      : template.triggerFieldChange === trigger.triggerFieldChange;
    if (!fieldChangeConsistent) {
      reasons.push(
        `Trigger ${i + 1} has a field change ("${trigger.triggerFieldChange}") that doesn't match its trigger type ("${trigger.templateId}")`
      );
    }

    const conditionConsistent = template.triggerConditionOptions?.length
      ? template.triggerConditionOptions.some(
          (o) => o.value === trigger.triggerCondition
        )
      : template.triggerCondition === trigger.triggerCondition;
    if (!conditionConsistent) {
      reasons.push(
        `Trigger ${i + 1} has a condition ("${trigger.triggerCondition}") that doesn't match its trigger type ("${trigger.templateId}")`
      );
    }

    // A triggerValueEditable: false template's triggerValue is fixed by
    // the catalog, not user input — the builder never renders a control
    // for it. Checking only triggerField/triggerCondition above lets an
    // imported (or LLM-authored) file swap in the OPPOSITE fixed value
    // while keeping the template id: "trigger-001-9" (displays as "And is
    // a drop-off", isWaiter === false) imported with triggerValue: true
    // passes every check above and then EVALUATES as "is a waiter" — a
    // silent label/behavior inversion (WEBV3-216). Editable templates are
    // exempt: their triggerValue is genuinely user-supplied, and is
    // instead checked against the target shop's live entities below
    // (derivedRefs).
    if (
      template.triggerValueEditable === false &&
      trigger.triggerValue !== template.triggerValue
    ) {
      reasons.push(
        `Trigger ${i + 1} has a value ("${trigger.triggerValue}") that doesn't match its trigger type ("${trigger.templateId}") — expected ("${template.triggerValue}")`
      );
    }
  });

  // Cross-trigger fixed-value contradiction (WEBV3-216). The per-trigger
  // check above catches a trigger whose OWN value disagrees with its OWN
  // template — but two different triggers, each individually valid, can
  // still contradict each other: trigger-001-1 ("And is a waiter",
  // isWaiter: true) and trigger-001-9 ("And is a drop-off", isWaiter:
  // false) each match their own template exactly, so the per-trigger loop
  // passes both, yet together they require isWaiter === true AND
  // isWaiter === false — never satisfiable under "AND". The builder's
  // condition picker (getSelectableConditionalTriggers) can't construct
  // this, but an imported or LLM-authored file can, and the import dialog
  // is unconditionally available with no feature flag. Reuses the same
  // predicate the builder's own save-time guard uses
  // (hasContradictoryFixedValueConditions), fed the resolved TEMPLATE's
  // triggerValueEditable for each trigger (import triggers don't carry
  // that field themselves — it's a catalog property, not part of the
  // imported file's own trigger shape).
  //
  // Gated on "AND": under "OR" the exact same pair is a tautology, not a
  // contradiction (isWaiter === true OR isWaiter === false matches every
  // card) — rejecting it would be a false positive with a backwards error
  // message. hasContradictoryFixedValueConditions has no opinion on
  // triggersConditional (it's a plain trigger-shape predicate reused
  // as-is from the builder's own AND-only save guard), so the AND check
  // belongs here, at the one call site that also sees OR automations.
  if (
    automation.triggersConditional === "AND" &&
    hasContradictoryFixedValueConditions(fixedValueShapes)
  ) {
    reasons.push(
      'These conditions can never both be true at once — this automation combines contradictory fixed-value conditions (e.g. "is a waiter" and "is a drop-off")'
    );
  }

  // Cross-trigger rule (every other check above is per-trigger). Promise-time
  // automations are evaluated by the scheduled promise-time scanner, which
  // only understands promise-time triggers, and the reactive onCardWrite path
  // never fires them — so a non-promise trigger sitting alongside one is never
  // evaluated by anything. Importing "WHEN promise time is overdue AND
  // container is Waiting" would therefore fire for EVERY overdue card in the
  // shop, silently. The builder can't construct this shape, but a hand-written
  // or LLM-generated file can (the prompt tells the model each trigger is
  // evaluated independently), so reject it here rather than let it through.
  const promiseTimeTriggerCount = automation.triggers.filter(
    (t) => t.triggerEventType === "onPromiseTime"
  ).length;
  if (
    promiseTimeTriggerCount > 0 &&
    promiseTimeTriggerCount < automation.triggers.length
  ) {
    reasons.push(
      "Promise time triggers can't be combined with other conditions — this automation mixes promise time triggers with other trigger types"
    );
  }

  // Parenthood. Every check above validates a trigger against its OWN
  // template, which is not enough: a conditional trigger only means anything
  // underneath the base trigger it was authored for. A file pairing
  // "trigger-008" (on a schedule) with "trigger-001-2" (a condition belonging
  // to "on card creation") passes every per-trigger check — both are
  // internally consistent — and produces an automation whose conditions are
  // evaluated NOWHERE: the card-write path refuses to fire a scheduled
  // automation, and the scanner would have to either ignore the orphan
  // condition (sweeping every open repair order) or apply it. Reject the file
  // instead of relying on either side to guess. This is reachable from the
  // import dialog's paste field, which is exactly where LLM-authored JSON
  // arrives.
  const parentsByConditionalId = new Map<string, string[]>();
  const indexParents = (parent: BuilderTrigger): void => {
    parent.conditionalTriggers?.forEach((child) => {
      parentsByConditionalId.set(child.id, [
        ...(parentsByConditionalId.get(child.id) ?? []),
        parent.id,
      ]);
      indexParents(child);
    });
  };
  AUTOMATION_TRIGGER_OPTIONS.forEach(indexParents);

  const presentTemplateIds = new Set(
    automation.triggers.map((t) => t.templateId)
  );
  // Scoped deliberately to automations that DO carry a base trigger. A file
  // containing only conditionals is a different (pre-existing) shape — an
  // "onCreate" conditional on its own does fire on the card-write path today,
  // so rejecting it here would break importing automations that work. What
  // cannot be allowed is a base trigger sitting next to a condition belonging
  // to a DIFFERENT base; that is the pairing nothing downstream can honour.
  const hasBaseTrigger = automation.triggers.some(
    (t) =>
      !parentsByConditionalId.has(t.templateId) &&
      flatTriggers.some((template) => template.id === t.templateId)
  );
  if (hasBaseTrigger) {
    automation.triggers.forEach((trigger, i) => {
      const parents = parentsByConditionalId.get(trigger.templateId);
      // No entry = a top-level trigger, or a removed one already reported.
      if (!parents) return;
      if (parents.some((parentId) => presentTemplateIds.has(parentId))) return;
      reasons.push(
        `Trigger ${i + 1} is a condition ("${trigger.templateId}") that belongs to a different trigger type than this automation's trigger`
      );
    });
  }

  automation.actions.forEach((action, i) => {
    const template = flatActions.find((a) => a.id === action.templateId);
    if (!template) {
      reasons.push(
        `Action ${i + 1} uses a removed action type ("${action.templateId}")`
      );
      return;
    }
    const fieldConsistent = template.actionFieldOptions?.length
      ? template.actionFieldOptions.some((o) => o.value === action.actionField)
      : template.actionField === action.actionField;
    if (!fieldConsistent) {
      reasons.push(
        `Action ${i + 1} has a field ("${action.actionField}") that doesn't match its action type ("${action.templateId}")`
      );
    }

    // actionEventType has no options-array counterpart on BuilderAction (the
    // type has no `actionEventTypeOptions` field at all) — it's always a
    // strict match against the template's fixed value.
    if (template.actionEventType !== action.actionEventType) {
      reasons.push(
        `Action ${i + 1} has an event type ("${action.actionEventType}") that doesn't match its action type ("${action.templateId}")`
      );
    }

    // actionObjectIdentifier is deliberately NOT validated: BuilderAction has
    // no such field, so there is nothing on the template to compare against.
    // The builder always writes "self" for it (see AutomationDialog.tsx) —
    // it isn't a template-driven choice, just a fixed constant.
  });

  // Re-derive remappable references from the ACTUAL trigger/action values —
  // never from the file-supplied resolvedReferences. A file can strip or
  // empty resolvedReferences and still pass, or carry stale metadata for a
  // since-edited value; only re-deriving from the real triggers/actions
  // closes both holes. The parsed triggers/actions are structurally
  // validated by zod and catalog-checked above, but their field types are
  // wider than Automation's — cast at this read boundary (mirrors the write
  // boundary cast in repositories/automation.ts's applyImportPlan).
  const derivedRefs = resolveAutomationReferences(
    {
      triggers: automation.triggers as Automation["triggers"],
      actions: automation.actions as Automation["actions"],
    },
    generateOptions
  );

  derivedRefs.forEach((ref) => {
    const options = generateOptions(undefined, ref.dynamicType);
    if (options?.some((o) => o.value === ref.value)) return;

    // The file's resolvedReferences has zero influence on pass/fail here —
    // it's consulted only as a label source. The derived ref's own label
    // reflects the TARGET shop and is "" for anything missing there, so
    // borrow the file's (source-shop) label for nicer display text if one
    // exists at the same position. That label can be stale (e.g. the file
    // was hand-edited after export) — it's cosmetic only.
    const fileRef = automation.resolvedReferences.find(
      (r) =>
        r.kind === ref.kind &&
        r.index === ref.index &&
        r.dynamicType === ref.dynamicType
    );
    const label = fileRef?.label || ref.label || ref.value;
    reasons.push(
      `${ref.kind === "trigger" ? "Trigger" : "Action"} ${ref.index + 1} references a ${ref.dynamicType.replace(/s$/, "")} ("${label}") that doesn't exist in this shop`
    );
  });

  return reasons;
}

export type ImportAction =
  | {
      kind: "create";
      automation: ParsedAutomationExportFile["automations"][number];
    }
  | {
      kind: "update";
      id: string;
      automation: ParsedAutomationExportFile["automations"][number];
    }
  | {
      kind: "reject";
      automation: ParsedAutomationExportFile["automations"][number];
      reasons: string[];
    };

export interface ImportPlan {
  mode: "add" | "replaceAll";
  actions: ImportAction[];
  /** Non-empty only for replaceAll — every current automation id, regardless of rejections. */
  deleteIds: string[];
}

export function planAutomationImport(
  imported: ParsedAutomationExportFile["automations"],
  currentAutomations: Pick<Automation, "id">[],
  mode: "add" | "replaceAll",
  generateOptions: GenerateOptions
): ImportPlan {
  const currentIds = new Set(currentAutomations.map((a) => a.id));
  const actions: ImportAction[] = imported.map((automation) => {
    const reasons = validateAutomationForImport(automation, generateOptions);
    if (reasons.length > 0) return { kind: "reject", automation, reasons };
    if (
      mode === "add" &&
      automation.id !== undefined &&
      currentIds.has(automation.id)
    ) {
      return { kind: "update", id: automation.id, automation };
    }
    return { kind: "create", automation };
  });

  return {
    mode,
    actions,
    deleteIds: mode === "replaceAll" ? currentAutomations.map((a) => a.id) : [],
  };
}

export const referenceMappingKey = (
  dynamicType: DynamicOptions,
  value: string
): string => `${dynamicType}:${value}`;

export type ReferenceMappings = Record<string, string>;

export interface UnresolvedEntity {
  key: string;
  dynamicType: DynamicOptions;
  sourceValue: string;
  /** Human-readable source-shop name; cosmetic only. */
  sourceLabel: string;
  /** Number of trigger/action occurrences across the file. */
  count: number;
}

export function collectUnresolvedReferences(
  automations: ParsedAutomationExportFile["automations"],
  generateOptions: GenerateOptions
): UnresolvedEntity[] {
  const byKey = new Map<string, UnresolvedEntity>();

  automations.forEach((automation) => {
    // Re-derive remappable refs from the ACTUAL triggers/actions — never the
    // file's resolvedReferences. Parsed field types are wider than
    // Automation's, so cast at this read boundary (mirrors
    // validateAutomationForImport).
    const refs = resolveAutomationReferences(
      {
        triggers: automation.triggers as Automation["triggers"],
        actions: automation.actions as Automation["actions"],
      },
      generateOptions
    );

    refs.forEach((ref) => {
      const options = generateOptions(undefined, ref.dynamicType);
      if (options?.some((o) => o.value === ref.value)) return; // present in target

      const key = referenceMappingKey(ref.dynamicType, ref.value);
      const existing = byKey.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }

      // Borrow the source-shop label from the file's resolvedReferences for
      // display only; fall back to the derived label, then the raw value.
      const fileRef = automation.resolvedReferences.find(
        (r) => r.dynamicType === ref.dynamicType && r.value === ref.value
      );
      byKey.set(key, {
        key,
        dynamicType: ref.dynamicType,
        sourceValue: ref.value,
        sourceLabel: fileRef?.label || ref.label || ref.value,
        count: 1,
      });
    });
  });

  return Array.from(byKey.values());
}

export function applyReferenceMappings(
  automations: ParsedAutomationExportFile["automations"],
  mappings: ReferenceMappings,
  generateOptions: GenerateOptions
): ParsedAutomationExportFile["automations"] {
  return automations.map((automation) => {
    const refs = resolveAutomationReferences(
      {
        triggers: automation.triggers as Automation["triggers"],
        actions: automation.actions as Automation["actions"],
      },
      generateOptions
    );
    if (refs.length === 0) return automation;

    const triggers = automation.triggers.map((t) => ({ ...t }));
    const actions = automation.actions.map((a) => ({ ...a }));
    let changed = false;

    refs.forEach((ref) => {
      const target = mappings[referenceMappingKey(ref.dynamicType, ref.value)];
      if (!target) return; // undefined or "" ⇒ leave unmapped
      if (ref.kind === "trigger") {
        triggers[ref.index].triggerValue = target;
      } else {
        actions[ref.index].actionValue = target;
      }
      changed = true;
    });

    if (!changed) return automation;
    return { ...automation, triggers, actions };
  });
}
